import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import { formatDateToLocal, getEvidenceDisplayName, ActorNameInput } from '@safetyhub/shared';
import { getUserIdentifier } from '../../../lib/organizations';
import { siteIndustryLabel } from '../../../lib/siteIndustry';
import { inspectionStatusLabels, inspectionStatusTones, InspectionListItem } from '../../../lib/inspections';
import { Report, getDocStatusBadgeTone } from '../../../lib/reports';
import { resolveReportStatusLabel } from '../../../lib/reportStatusLabel';
import { ChevronRightIcon, UserGroupIcon, ClockIcon, XCircleIcon } from './components/Icons';
import { SiteContactsSection } from './components/SiteContactsSection';
import { SiteAgencyLinksSection } from './components/SiteAgencyLinksSection';
import { SiteOperatorGroupItem } from './components/SiteOperatorGroupItem';
import { SiteLocationsSection } from './components/SiteLocationsSection';
import { SiteDetailLocationsSection } from './components/SiteDetailLocationsSection';
import { SiteProcessTypesSection } from './components/SiteProcessTypesSection';
import { SiteEquipmentSection } from './components/SiteEquipmentSection';
import { SiteBusinessOverviewTab } from './components/SiteBusinessOverviewTab';
import { MasterSectionHeader } from './components/MasterSectionHeader';
import { SiteApprovalLinesSection } from './components/SiteApprovalLinesSection';
import { SiteDetailLayoutProps } from './types';
import { useMasterExpanded, useMasterCounts } from './useMasterExpanded';
import { memberDisplayName } from '../../../lib/members';

/**
 * 미해결 누적 추이선 색 — brand-500. SVG `stroke`는 Tailwind 토큰 클래스를 받지 못해
 * 값으로 적어야 한다(Dashboard 차트도 같은 hex를 쓴다).
 */
const BRAND_500 = '#6366f1';

/**
 * SiteDetail 데스크톱 레이아웃
 */
export function SiteDetailDesktop({
  data,
  companyId,
  siteId,
  state,
  handlers,
  permissions,
}: SiteDetailLayoutProps) {
  const { site, assignments, members, siteInvitations, findingsTrend, siteStats, recentInspections, recentReports } =
    data;
  const {
    trendPeriod,
    setTrendPeriod,
    activeTab,
    setActiveTab,
    isResending,
  } = state;
  const { handleResendInvitation } = handlers;
  // 자리마다 키 하나(P4 ②): 하위 섹션 prop 이름 `isOwner`는 호환용이고 값은 canManageSite(site.manage)
  //
  // ⚠️ 즉 이 `isOwner`는 **권한 키 `site.manage`의 별칭**이고 소유권 **성질**(서버 `isOwnerMember`)이
  // 아니다 — 아래 `isOwner={isOwner}` 전달 자리들의 값도 전부 그 키다. 이름을 근거로 성질 축으로
  // 「정리」하면(`usePermissions().isOwner`로 바꾸면) 그 키만 가진 시드 안전관리자가 장소·공정·설비·
  // 담당자 섹션의 입구를 한꺼번에 잃는다 — 2026-09-24 실사고와 같은 형태다(서버는 허용하는데 화면에
  // 길이 없는 상태). 같은 이유로 결재선 prop은 2026-09-27에 `canManageApprovalLines`로 이름을 고쳤다.
  // 이 별칭 자체를 걷는 것(10곳+ rename)은 backlog. 배선 핀: `components/__tests__/rbacOwnerAxisWiringGuard.test.ts`.
  const { canManageSite: isOwner, canAssign, canInvite, canManageApprovalLines, unassignedMembers } = permissions;

  const { detailTab, setDetailTab } = state;
  const [isMasterExpanded, toggleMaster] = useMasterExpanded();
  const masterCounts = useMasterCounts(companyId, siteId, detailTab === 'management');

  return (
    <>
    {/* 페이지 탭 헤더 */}
    <div className="border-b border-line mb-6">
      <nav className="-mb-px flex space-x-8">
        <button
          onClick={() => setDetailTab('basic')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            detailTab === 'basic'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
          }`}
        >
          기본 정보
        </button>
        <button
          onClick={() => setDetailTab('overview')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            detailTab === 'overview'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
          }`}
        >
          사업장 개요
        </button>
        <button
          onClick={() => setDetailTab('management')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            detailTab === 'management'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
          }`}
        >
          사업장 관리
        </button>
        <button
          onClick={() => setDetailTab('approval')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            detailTab === 'approval'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
          }`}
        >
          결재선
        </button>
      </nav>
    </div>

    {detailTab === 'approval' ? (
      <SiteApprovalLinesSection
        companyId={companyId}
        siteId={siteId}
        canManageApprovalLines={canManageApprovalLines}
      />
    ) : detailTab === 'overview' ? (
      <SiteBusinessOverviewTab
        companyId={companyId}
        siteId={siteId}
        site={site}
        isOwner={isOwner}
      />
    ) : detailTab === 'management' ? (
      <div className="space-y-6">
        {/* 대상 구성 마스터 (대상 생성에 사용되는 선택지) — 펼침/접힘 가능 */}
        <section>
          <MasterSectionHeader
            expanded={isMasterExpanded}
            onToggle={toggleMaster}
            processCount={masterCounts.processCount}
            locationCount={masterCounts.locationCount}
            detailLocationCount={masterCounts.detailLocationCount}
          />
          {isMasterExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <SiteProcessTypesSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
              <SiteLocationsSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
              <SiteDetailLocationsSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
            </div>
          )}
        </section>

        {/* 대상 (위 마스터 값을 참조하여 등록된 실제 객체) — 풀폭 */}
        <section>
          <SiteEquipmentSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
        </section>
      </div>
    ) : (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* 현장 정보 */}
      <div className="lg:col-span-2 space-y-6">
        {/* 기본 정보 카드 */}
        <Card padding="xl" radius="lg" elevation="md">
          <h2 className="text-lg font-medium text-ink-900 mb-4">기본 정보</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-ink-500">업종</dt>
              <dd className="mt-1 text-sm text-ink-900">
                {siteIndustryLabel(site) ?? '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-ink-500">시간대</dt>
              <dd className="mt-1 text-sm text-ink-900">{site.timezone || '조직 기본값'}</dd>
            </div>
            <SiteOperatorGroupItem
              site={site}
              dtClassName="text-sm font-medium text-ink-500"
              ddClassName="mt-1 text-sm text-ink-900"
            />
            {(site.latitude || site.longitude) && (
              <div>
                <dt className="text-sm font-medium text-ink-500">좌표</dt>
                <dd className="mt-1 text-sm text-ink-900">
                  {site.latitude}, {site.longitude}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-ink-500">등록일</dt>
              <dd className="mt-1 text-sm text-ink-900">
                {formatDateToLocal(new Date(site.createdAt))}
              </dd>
            </div>
          </dl>

          {/* 위험 카테고리 */}
          {site.hazardCategories && site.hazardCategories.length > 0 && (
            <div className="mt-6 pt-4 border-t border-line">
              <dt className="text-sm font-medium text-ink-500 mb-2">위험 카테고리</dt>
              <dd className="flex flex-wrap gap-2">
                {site.hazardCategories.map((cat) => (
                  <Badge key={cat} tone="amber">
                    {cat}
                  </Badge>
                ))}
              </dd>
            </div>
          )}
        </Card>

        {/* 통계 카드 */}
        <Card padding="xl" radius="lg" elevation="md">
          <h2 className="text-lg font-medium text-ink-900 mb-4">현장 통계</h2>
          <div className="grid grid-cols-3 gap-4">
            <Link
              to={`/inspections?siteId=${siteId}`}
              className="text-center p-4 bg-brand-50 rounded-card hover:bg-brand-100 transition-colors"
            >
              <p className="text-2xl font-bold text-brand-600">
                {siteStats?.inspections.total ?? '-'}
              </p>
              <p className="text-sm text-ink-600">점검</p>
              {siteStats && siteStats.inspections.inProgress > 0 && (
                <p className="text-xs text-brand-500 mt-1">
                  진행 중 {siteStats.inspections.inProgress}
                </p>
              )}
            </Link>
            <Link
              to={`/reports?siteId=${siteId}`}
              className="text-center p-4 bg-green-50 rounded-card hover:bg-green-100 transition-colors"
            >
              <p className="text-2xl font-bold text-green-600">
                {siteStats?.reports.total ?? '-'}
              </p>
              <p className="text-sm text-ink-600">보고서</p>
              {siteStats && siteStats.reports.pendingApproval > 0 && (
                <p className="text-xs text-yellow-600 mt-1">
                  결재대기 {siteStats.reports.pendingApproval}
                </p>
              )}
            </Link>
            {/* TBM — 서버 `getSiteStats().tbms`(4버킷)를 그대로 보인다.
                ⚠️ 이웃 둘과 달리 **링크가 아니다.** 문서관리 목록에는 「TBM만」 거르는 필터가
                없다(구 `type=TBM` 탭은 전환과 함께 은퇴했고, `type=COMPOSED`는 다른 양식까지
                포함한다). 링크를 걸면 **숫자와 도착지가 어긋나** 표시가 거짓이 된다.
                목록 필터가 양식 축을 갖게 되면 그때 링크로 바꾼다.
                (2026-09-09 실측 F-4: 서버는 세는데 화면이 `-`/「준비중」을 하드코딩하고 있었다.) */}
            <div className="text-center p-4 bg-surface-soft rounded-card">
              <p className="text-2xl font-bold text-ink-700">
                {siteStats?.tbms.total ?? '-'}
              </p>
              <p className="text-sm text-ink-600">TBM</p>
              {siteStats && siteStats.tbms.inProgress > 0 && (
                <p className="text-xs text-brand-500 mt-1">
                  교육 중 {siteStats.tbms.inProgress}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* 위험요인 추이 그래프 */}
        {findingsTrend && findingsTrend.periods.length > 0 && (
          <Card padding="xl" radius="lg" elevation="md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-medium text-ink-900">위험요인 추이</h2>
                {/* 기간 선택 탭 */}
                <div className="flex bg-ink-100 rounded-control p-0.5">
                  {(
                    [
                      { key: 'daily', label: '7일' },
                      { key: 'weekly', label: '5주' },
                      { key: 'monthly', label: '6개월' },
                    ] as const
                  ).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setTrendPeriod(key)}
                      className={`px-3 py-1 text-xs font-medium rounded-card-sm transition-colors ${
                        trendPeriod === key
                          ? 'bg-white text-ink-900 shadow-sm'
                          : 'text-ink-500 hover:text-ink-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 신규 발견 (단일 막대) */}
              <div>
                <h3 className="text-sm font-medium text-ink-700 mb-3">신규 발견</h3>
                <div className="space-y-2">
                  {findingsTrend.periods.map((period, idx) => {
                    const maxValue = Math.max(
                      ...findingsTrend.periods.map((p) => p.discovered),
                      1
                    );
                    const width = (period.discovered / maxValue) * 100;

                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span
                          className="text-xs text-ink-500 w-20 flex-shrink-0 truncate"
                          title={period.label}
                        >
                          {period.label}
                        </span>
                        <div className="flex-1 flex items-center gap-1">
                          <div className="flex-1 bg-ink-100 rounded-full h-4 overflow-hidden">
                            <div
                              className="h-4 rounded-full bg-brand-500 transition-all"
                              style={{ width: `${width}%` }}
                              title={`신규 발견: ${period.discovered}`}
                            />
                          </div>
                          <span className="text-xs text-ink-600 w-6 text-right">{period.discovered}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-ink-400">
                  * 해결 현황은 미해결 누적 그래프의 감소로 확인
                </p>
              </div>

              {/* 미해결 누적 추이 (단일 라인) */}
              <div>
                <h3 className="text-sm font-medium text-ink-700 mb-3">미해결 누적</h3>
                <div className="flex">
                  <div className="flex-1 relative h-32">
                    {(() => {
                      const maxValue = Math.max(
                        ...findingsTrend.periods.map((p) => p.unresolved),
                        1
                      );
                      const midValue = maxValue / 2;
                      const isOdd = midValue !== Math.floor(midValue);
                      const adjustedMax = isOdd ? Math.ceil(midValue) * 2 : maxValue;

                      const getXPercent = (idx: number) =>
                        (idx / (findingsTrend.periods.length - 1 || 1)) * 100;
                      const getYPercent = (value: number) => (value / adjustedMax) * 85;

                      const points = findingsTrend.periods.map((period, idx) => ({
                        x: getXPercent(idx),
                        y: 100 - getYPercent(period.unresolved),
                      }));

                      const pathD = points
                        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                        .join(' ');

                      return (
                        <>
                          <svg
                            className="w-full h-full"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                          >
                            <line x1="0" y1="15" x2="100" y2="15" stroke="#e8ecf2" strokeWidth="0.5" />
                            <line x1="0" y1="57.5" x2="100" y2="57.5" stroke="#e8ecf2" strokeWidth="0.5" />
                            <line x1="0" y1="100" x2="100" y2="100" stroke="#e8ecf2" strokeWidth="0.5" />
                            <path
                              d={pathD}
                              fill="none"
                              stroke={BRAND_500}
                              strokeWidth="2"
                              vectorEffect="non-scaling-stroke"
                            />
                          </svg>
                          {findingsTrend.periods.map((period, idx) => (
                            <div
                              key={idx}
                              className="absolute w-2.5 h-2.5 rounded-full border-2 border-white bg-brand-500"
                              style={{
                                left: `${getXPercent(idx)}%`,
                                bottom: `${getYPercent(period.unresolved)}%`,
                                transform: 'translate(-50%, 50%)',
                              }}
                              title={`${period.label}: ${period.unresolved}`}
                            />
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  <div className="w-8 h-32 flex flex-col justify-between text-xs text-ink-400 text-right pl-1">
                    {(() => {
                      const maxValue = Math.max(
                        ...findingsTrend.periods.map((p) => p.unresolved),
                        1
                      );
                      const midValue = maxValue / 2;
                      const isOdd = midValue !== Math.floor(midValue);
                      const adjustedMid = isOdd ? Math.ceil(midValue) : midValue;
                      const displayMax = isOdd ? adjustedMid * 2 : maxValue;

                      return (
                        <>
                          <span>{displayMax}</span>
                          <span>{adjustedMid}</span>
                          <span>0</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-1 pr-8">
                  {findingsTrend.periods.map((period, idx) => (
                    <div key={idx} className="text-center">
                      <span className="block text-ink-500">{period.label}</span>
                      <span className="font-medium text-brand-600" style={{ fontSize: '10px' }}>
                        {period.unresolved}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

      </div>

      {/* 우측 컬럼 - 담당자 그룹 */}
      <div className="space-y-4">
        {/* 점검 담당자 */}
        <Card padding="xl" radius="lg" elevation="md">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-ink-900">점검 담당자</h2>
              <p className="text-xs text-ink-400 mt-1">이 현장에 배정된 안전관리자 목록</p>
            </div>
            {canAssign && unassignedMembers.length > 0 && (
              <button
                onClick={() => state.setShowAssignModal(true)}
                className="text-sm text-brand-600 hover:text-brand-800"
              >
                + 추가
              </button>
            )}
          </div>

          {assignments.length === 0 ? (
            <div className="text-center py-3">
              <UserGroupIcon className="mx-auto h-8 w-8 text-ink-300" />
              <p className="mt-2 text-sm text-ink-500">할당된 담당자가 없습니다.</p>
              {canAssign && unassignedMembers.length > 0 && (
                <button
                  onClick={() => state.setShowAssignModal(true)}
                  className="mt-2 text-sm text-brand-600 hover:text-brand-800"
                >
                  담당자 추가하기
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-line max-h-48 overflow-y-auto overflow-x-hidden">
              {assignments.map((assignment) => {
                const member = members.find((m) => m.id === assignment.memberId);
                if (!member) return null;

                const isPrimary = assignment.assignmentType === 'PRIMARY';

                return (
                  <li
                    key={assignment.id}
                    className="py-3 flex items-center cursor-pointer hover:bg-surface-hover -mx-2 px-2 rounded-card transition-colors"
                    onClick={() => state.setSelectedAssignment(assignment)}
                  >
                    {/* 아바타 */}
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                      <span className="text-brand-600 font-medium">
                        {memberDisplayName(member, 'U').charAt(0)}
                      </span>
                    </div>

                    {/* 정보 */}
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {/* 이름 정본은 Member — user는 로그인 계정이 있어야만 존재한다(lib/members). */}
                        <p className="text-sm font-medium text-ink-900 truncate">
                          {memberDisplayName(member)}
                        </p>
                        {/* 정(PRIMARY)/부(SECONDARY)는 색으로 가르는 짝이라 tone만 다르게 준다 */}
                        <Badge
                          tone={isPrimary ? 'brand' : 'slate'}
                          shape="rounded"
                          size="sm"
                          className="flex-shrink-0"
                        >
                          {isPrimary ? '정' : '부'}
                        </Badge>
                      </div>
                      <p className="text-xs text-ink-500 truncate">
                        {getUserIdentifier(member.user?.auths ?? [])}
                      </p>
                    </div>

                    {/* 화살표 */}
                    <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400 ml-2" />
                  </li>
                );
              })}
            </ul>
          )}

          {/* 초대 현황 */}
          {canInvite && siteInvitations.length > 0 && (
            <div className="mt-4 pt-4 border-t border-line">
              <h3 className="text-sm font-medium text-ink-700 mb-3">초대 현황</h3>
              <ul className="space-y-2">
                {siteInvitations.map((invitation) => {
                  const isPending = invitation.status === 'PENDING';
                  const isRejected = invitation.status === 'REJECTED';
                  const isExpired = invitation.status === 'EXPIRED';

                  return (
                    <li
                      key={invitation.id}
                      className={`py-2 flex items-center justify-between ${!isPending ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            isPending
                              ? 'bg-yellow-100'
                              : isRejected
                                ? 'bg-red-100'
                                : 'bg-ink-200'
                          }`}
                        >
                          {isPending ? (
                            <ClockIcon className="h-4 w-4 text-yellow-600" />
                          ) : isRejected ? (
                            <XCircleIcon className="h-4 w-4 text-red-500" />
                          ) : (
                            <ClockIcon className="h-4 w-4 text-ink-400" />
                          )}
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm ${!isPending ? 'text-ink-500' : 'text-ink-900'}`}>
                            {invitation.email ||
                              invitation.phone ||
                              (invitation.username && `@${invitation.username}`)}
                            {isRejected && (
                              <span className="ml-2 text-xs text-red-600 font-medium">거절됨</span>
                            )}
                            {isExpired && (
                              <span className="ml-2 text-xs text-ink-500 font-medium">만료됨</span>
                            )}
                          </p>
                          <p className="text-xs text-ink-500">
                            {isPending ? '만료' : isRejected ? '거절일' : '만료일'}:{' '}
                            {formatDateToLocal(
                              new Date(isPending ? invitation.expiresAt : (invitation.updatedAt ?? invitation.createdAt))
                            )}
                          </p>
                        </div>
                      </div>
                      {isPending && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleResendInvitation(invitation)}
                            disabled={isResending === invitation.id}
                            className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-50"
                          >
                            {isResending === invitation.id ? '발송 중...' : '재발송'}
                          </button>
                          <button
                            onClick={() => state.setInvitationCancelTarget(invitation)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            취소
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Card>

        {/* 현장 담당자 */}
        <SiteContactsSection
          companyId={companyId}
          siteId={siteId}
          isOwner={isOwner}
        />

        {/* 대행 기관(P4) — 플래그 꺼짐·링크 0건이면 섹션째 안 그린다 */}
        <SiteAgencyLinksSection companyId={companyId} siteId={siteId} />
      </div>
    </div>
    )}

    {detailTab === 'basic' && (
      <Card padding="xl" radius="lg" elevation="md" className="mt-6">
        <div className="border-b border-line mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('inspections')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                activeTab === 'inspections'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
              }`}
            >
              점검 ({siteStats?.inspections.total ?? 0})
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                activeTab === 'reports'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
              }`}
            >
              보고서 ({siteStats?.reports.total ?? 0})
            </button>
            <button
              onClick={() => setActiveTab('tbm')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                activeTab === 'tbm'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
              }`}
            >
              TBM <span className="text-ink-400">(준비중)</span>
            </button>
          </nav>
        </div>

        {/* 점검 탭 */}
        {activeTab === 'inspections' && (
          <div>
            {!recentInspections || recentInspections.length === 0 ? (
              <p className="text-sm text-ink-500 text-center py-4">점검 내역이 없습니다.</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentInspections.map((inspection: InspectionListItem) => (
                  <li key={inspection.id} className="py-3">
                    <Link
                      to={`/inspections/${inspection.id}`}
                      className="flex items-center justify-between hover:bg-surface-hover -mx-2 px-2 py-1 rounded-card transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-900 truncate">
                          {site.name} {inspection.sequenceNumber ?? 1}차 점검
                        </p>
                        <p className="text-xs text-ink-500">
                          {formatDateToLocal(new Date(inspection.createdAt))}
                          {inspection.photoCount > 0 && ` · 사진 ${inspection.photoCount}장`}
                          {inspection.hazardCount > 0 && ` · 위험 ${inspection.hazardCount}건`}
                        </p>
                      </div>
                      <Badge tone={inspectionStatusTones[inspection.status]} shape="rounded" className="ml-2">
                        {inspectionStatusLabels[inspection.status]}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {recentInspections && recentInspections.length > 0 && (
              <div className="mt-3 text-center">
                <Link
                  to={`/inspections?siteId=${siteId}`}
                  className="text-sm text-brand-600 hover:text-brand-800"
                >
                  전체 보기
                </Link>
              </div>
            )}
          </div>
        )}

        {/* 보고서 탭 */}
        {activeTab === 'reports' && (
          <div>
            {!recentReports || recentReports.length === 0 ? (
              <p className="text-sm text-ink-500 text-center py-4">보고서가 없습니다.</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentReports.map((report: Report) => (
                  <li key={report.id} className="py-3">
                    <Link
                      to={`/reports/${report.id}`}
                      className="flex items-center justify-between hover:bg-surface-hover -mx-2 px-2 py-1 rounded-card transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-900 truncate">{report.title}</p>
                        <p className="text-xs text-ink-500">
                          {formatDateToLocal(new Date(report.createdAt))}
                          {' · '}
                          {getEvidenceDisplayName(
                            (report as any).authorSignerName ?? (report as any).authorSnapshotName ?? null,
                            report.author ? ({ name: report.author.member?.name ?? report.author.name, deletedAt: report.author.deletedAt } as ActorNameInput) : null,
                          )}
                        </p>
                      </div>
                      {/* 라벨은 report 데이터로 판정 — 결재 정책 없는 완료본은 '완료'(목록 응답도 서명 행을 싣는다). */}
                      <Badge tone={getDocStatusBadgeTone(report.status)} shape="rounded" className="ml-2">
                        {resolveReportStatusLabel(report)}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {recentReports && recentReports.length > 0 && (
              <div className="mt-3 text-center">
                <Link
                  to={`/reports?siteId=${siteId}`}
                  className="text-sm text-brand-600 hover:text-brand-800"
                >
                  전체 보기
                </Link>
              </div>
            )}
          </div>
        )}

        {/* TBM 탭 */}
        {activeTab === 'tbm' && (
          <div className="text-center py-8">
            <p className="text-ink-400 text-sm">TBM 기능은 준비중입니다.</p>
          </div>
        )}
      </Card>
    )}
    </>
  );
}



export default SiteDetailDesktop;
